import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import predixTourCopy from '@/constants/predixTourCopy.json'

export const TOUR_STORAGE_PREFIX = 'metalnx_predix_tour_done_'
export const TOUR_PENDING_KEY = 'metalnx_predix_tour_pending'
export const PREDIX_TOUR_ROUTE = '/panel/predix'
export const TOUR_STEP_TOTAL = predixTourCopy.length

let activeDriver = null

function tourCopy(stepIndex) {
  const entry = predixTourCopy[stepIndex]
  if (!entry) return { title: '', description: '' }
  return {
    title: entry.title || '',
    description: entry.description || '',
  }
}

function storageKey(username) {
  return `${TOUR_STORAGE_PREFIX}${username || 'anonymous'}`
}

export function hasCompletedUserTour(username) {
  try {
    return localStorage.getItem(storageKey(username)) === '1'
  } catch (e) {
    return false
  }
}

export function markUserTourCompleted(username) {
  try {
    localStorage.setItem(storageKey(username), '1')
  } catch (e) {
    /* ignore */
  }
}

export function setPendingUserTour(pending = true) {
  try {
    if (pending) sessionStorage.setItem(TOUR_PENDING_KEY, '1')
    else sessionStorage.removeItem(TOUR_PENDING_KEY)
  } catch (e) {
    /* ignore */
  }
}

export function consumePendingUserTour() {
  try {
    const pending = sessionStorage.getItem(TOUR_PENDING_KEY) === '1'
    if (pending) sessionStorage.removeItem(TOUR_PENDING_KEY)
    return pending
  } catch (e) {
    return false
  }
}

function isOnPredixRoute(router) {
  const path = router && router.currentRoute ? router.currentRoute.path : ''
  return typeof path === 'string' && (path === PREDIX_TOUR_ROUTE || path.startsWith('/panel/predix') || path.startsWith('/panel/ipredix'))
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms))
}

function waitForSelector(selector, { timeout = 120000, interval = 150 } = {}) {
  return new Promise((resolve) => {
    const started = Date.now()
    const tick = () => {
      const el = document.querySelector(selector)
      if (el) return resolve(el)
      if (Date.now() - started >= timeout) return resolve(null)
      setTimeout(tick, interval)
    }
    tick()
  })
}

function resolveTourElement(selector) {
  const parts = String(selector).split(',').map((s) => s.trim()).filter(Boolean)
  for (const part of parts) {
    if (document.querySelector(part)) return part
  }
  return null
}

function getEventBus() {
  try {
    // eslint-disable-next-line global-require
    const app = require('@/main').default
    return app && app.$eventBus
  } catch (e) {
    return null
  }
}

function destroyActiveTour() {
  if (!activeDriver) return
  try {
    activeDriver.destroy()
  } catch (e) {
    /* ignore */
  }
  activeDriver = null
}

function renameCloseButtonToSkip(popover) {
  if (!popover || !popover.closeButton) return
  popover.closeButton.textContent = 'Skip'
  popover.closeButton.setAttribute('aria-label', 'Skip')
}

function driverDefaults(extra = {}) {
  const { onPopoverRender: userOnPopoverRender, ...rest } = extra
  return {
    showProgress: true,
    animate: true,
    allowClose: true,
    overlayOpacity: 0.55,
    stagePadding: 8,
    stageRadius: 12,
    popoverClass: 'metalnx-tour-popover',
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Done',
    progressText: '{{current}} of {{total}}',
    onPopoverRender: (popover, opts) => {
      renameCloseButtonToSkip(popover)
      if (typeof userOnPopoverRender === 'function') userOnPopoverRender(popover, opts)
    },
    ...rest,
  }
}

async function emitTour(eventName) {
  const bus = getEventBus()
  if (bus) bus.$emit(eventName)
  await sleep(420)
}

const showMainDbForTour = () => emitTour('predix-tour-show-main')
const showBenchmarkDbForTour = () => emitTour('predix-tour-show-benchmark')
const clickStartPredictionForTour = () => emitTour('predix-tour-click-start-prediction')
const showPredictionsTabForTour = () => emitTour('predix-tour-show-predictions-tab')
const openFirstAccordionForTour = () => emitTour('predix-tour-open-first-accordion')
const expandFirstRowForTour = () => emitTour('predix-tour-expand-first-row')
const showComparisonTabForTour = () => emitTour('predix-tour-show-comparison-tab')
const selectComparisonPropertyForTour = () => emitTour('predix-tour-select-comparison-property')
const selectComparisonBenchmarkForTour = () => emitTour('predix-tour-select-comparison-benchmark')

function refreshTourPopover(d) {
  const obj = d || activeDriver
  if (obj && typeof obj.refresh === 'function') obj.refresh()
}

function goNextAfterPrepare(d, prepareFn) {
  return async () => {
    if (typeof prepareFn === 'function') await prepareFn()
    const idx = d.getActiveIndex()
    const steps = (d.getConfig() && d.getConfig().steps) || []
    const next = steps[idx + 1]
    if (next && next.desiredElement) {
      const resolved =
        resolveTourElement(next.desiredElement) ||
        (next.fallbackElement ? resolveTourElement(next.fallbackElement) : null)
      if (resolved) next.element = resolved
    }
    d.moveNext()
    // driver.js animates each highlight transition over ~400ms and only updates
    // its own internal "active element/step" state once that finishes. Calling
    // refresh() before then re-renders the popover using the PREVIOUS step's
    // stale element, which visibly snaps the box back one step. Wait past the
    // transition before nudging the position for any late layout shifts
    // (e.g. panel width/accordion transitions still settling).
    await sleep(450)
    refreshTourPopover(d)
  }
}

function materializeSteps(rawSteps) {
  // Never drop a step just because its target isn't in the DOM yet (e.g. it's
  // behind an async fetch, a tab switch, or an expand action performed by an
  // earlier step's onNextClick). Dropping steps here shifts every later
  // step's index, which either skips a step entirely (progress numbers jump,
  // e.g. "Main DB" -> "Alloy Comparison") or shows the wrong step's box.
  // Keep the desired selector as a placeholder `element` so driver.js can do
  // its own live lookup when it actually renders that step; the per-step
  // onNextClick handlers also re-resolve the *next* step's element right
  // before advancing, once its prepareFn has finished creating it.
  return rawSteps.map((step) => {
    const element =
      resolveTourElement(step.desiredElement) ||
      (step.fallbackElement ? resolveTourElement(step.fallbackElement) : null) ||
      step.desiredElement
    return { ...step, element }
  })
}

function withProgress(steps, offset, total) {
  return steps.map((step, index) => ({
    ...step,
    popover: {
      ...step.popover,
      progressText: `${offset + index + 1} of ${total}`,
      showProgress: true,
    },
  }))
}

/** Steps 1–7 */
function buildSetupSteps(driverObj, { username, markComplete }) {
  return withProgress(
    materializeSteps([
      {
        desiredElement: '[data-tour="predix-intro"]',
        popover: {
          ...tourCopy(0),
          side: 'right',
          align: 'start',
          onNextClick: goNextAfterPrepare(driverObj, showMainDbForTour),
        },
      },
      {
        desiredElement: '[data-tour="predix-main-db"]',
        popover: {
          ...tourCopy(1),
          side: 'bottom',
          align: 'center',
          onNextClick: goNextAfterPrepare(driverObj, async () => {
            await showMainDbForTour()
            await waitForSelector('[data-tour="predix-main-inputs"]', { timeout: 20000 })
          }),
        },
      },
      {
        desiredElement: '[data-tour="predix-main-inputs"]',
        popover: {
          ...tourCopy(2),
          side: 'right',
          align: 'start',
          onNextClick: goNextAfterPrepare(driverObj, async () => {
            await showMainDbForTour()
            await waitForSelector('[data-tour="predix-main-inputs"]', { timeout: 20000 })
            await waitForSelector('[data-tour="predix-start-prediction"]', { timeout: 5000 })
          }),
        },
      },
      {
        desiredElement: '[data-tour="predix-start-prediction"]',
        popover: {
          ...tourCopy(3),
          side: 'top',
          align: 'center',
          onNextClick: goNextAfterPrepare(driverObj, showMainDbForTour),
        },
      },
      {
        desiredElement: '[data-tour="predix-benchmark-db"]',
        popover: {
          ...tourCopy(4),
          side: 'right',
          align: 'start',
          onNextClick: goNextAfterPrepare(driverObj, async () => {
            await showBenchmarkDbForTour()
            // Switching tabs triggers an async fetch of the Standard alloy fields;
            // the real input isn't in the DOM until that finishes. Without this
            // wait the next step falls back to the Benchmark DB tab position
            // (looks "one step behind").
            await waitForSelector('[data-tour="predix-benchmark-input"]', { timeout: 20000 })
          }),
        },
      },
      {
        desiredElement: '[data-tour="predix-benchmark-input"]',
        fallbackElement: '[data-tour="predix-benchmark-db"]',
        popover: {
          ...tourCopy(5),
          side: 'right',
          align: 'start',
          onNextClick: goNextAfterPrepare(driverObj, async () => {
            await showBenchmarkDbForTour()
            await waitForSelector('[data-tour="predix-benchmark-input"]', { timeout: 20000 })
            await waitForSelector('[data-tour="predix-start-prediction"]', { timeout: 5000 })
          }),
        },
      },
      {
        desiredElement: '[data-tour="predix-start-prediction"]',
        popover: {
          ...tourCopy(6),
          side: 'top',
          align: 'center',
          onNextClick: async () => {
            await showBenchmarkDbForTour()
            await clickStartPredictionForTour()
            const ready = await waitForSelector(
              '[data-tour="predix-explore-accordion"], .predix-explore-panels .v-expansion-panel',
              { timeout: 120000 }
            )
            driverObj.destroy()
            activeDriver = null
            if (!ready) {
              if (markComplete) markUserTourCompleted(username)
              return
            }
            await sleep(400)
            await startResultsTour({ username, markComplete })
          },
        },
      },
    ]),
    0,
    TOUR_STEP_TOTAL
  )
}

/** Steps 8–14 */
function buildResultSteps(driverObj, { username = '', markComplete = true } = {}) {
  return withProgress(
    materializeSteps([
      {
        desiredElement: '[data-tour="predix-alloy-predictions"]',
        popover: {
          ...tourCopy(7),
          side: 'bottom',
          align: 'start',
          onNextClick: goNextAfterPrepare(driverObj, showPredictionsTabForTour),
        },
      },
      {
        desiredElement: '[data-tour="predix-explore-accordion"]',
        fallbackElement: '.predix-explore-panels .v-expansion-panel-header',
        popover: {
          ...tourCopy(8),
          side: 'left',
          align: 'start',
          onNextClick: goNextAfterPrepare(driverObj, async () => {
            await openFirstAccordionForTour()
            await waitForSelector('[data-tour="predix-explore-row"], .cer-invoice-data-table tbody tr', {
              timeout: 10000,
            })
          }),
        },
      },
      {
        desiredElement: '[data-tour="predix-explore-row"]',
        fallbackElement: '.cer-invoice-data-table tbody tr',
        popover: {
          ...tourCopy(9),
          side: 'left',
          align: 'start',
          onNextClick: goNextAfterPrepare(driverObj, async () => {
            await expandFirstRowForTour()
            await waitForSelector('[data-tour="predix-benchmark-against"]', { timeout: 10000 })
          }),
        },
      },
      {
        desiredElement: '[data-tour="predix-benchmark-against"]',
        popover: {
          ...tourCopy(10),
          side: 'left',
          align: 'start',
          onNextClick: goNextAfterPrepare(driverObj, showPredictionsTabForTour),
        },
      },
      {
        desiredElement: '[data-tour="predix-alloy-comparison"]',
        popover: {
          ...tourCopy(11),
          side: 'bottom',
          align: 'start',
          onNextClick: goNextAfterPrepare(driverObj, async () => {
            await showComparisonTabForTour()
            await waitForSelector('[data-tour="predix-comparison-properties"]', { timeout: 10000 })
          }),
        },
      },
      {
        desiredElement: '[data-tour="predix-comparison-properties"]',
        popover: {
          ...tourCopy(12),
          side: 'bottom',
          align: 'start',
          onNextClick: goNextAfterPrepare(driverObj, async () => {
            await selectComparisonPropertyForTour()
            await waitForSelector('[data-tour="predix-comparison-benchmark-alloy"]', { timeout: 5000 })
          }),
        },
      },
      {
        desiredElement: '[data-tour="predix-comparison-benchmark-alloy"]',
        popover: {
          ...tourCopy(13),
          side: 'bottom',
          align: 'start',
          onNextClick: async () => {
            await selectComparisonBenchmarkForTour()
            await sleep(350)
            if (markComplete) markUserTourCompleted(username)
            driverObj.destroy()
            activeDriver = null
          },
        },
      },
    ]),
    7,
    TOUR_STEP_TOTAL
  )
}

async function prepareSidebar(prepareSidebarCb) {
  if (typeof prepareSidebarCb === 'function') prepareSidebarCb()
  else {
    try {
      localStorage.setItem('sidebar-expanded', 'true')
    } catch (e) {
      /* ignore */
    }
    document.body.classList.add('sidebar-expanded')
  }
  await sleep(220)
}

async function expandPredixCompositionPanel() {
  const aside = document.querySelector('[data-tour="predix-composition-aside"]')
  if (aside && aside.classList.contains('predix-composition-aside--collapsed')) {
    const expandBtn = aside.querySelector('[aria-label="Expand composition panel"]')
    if (expandBtn) expandBtn.click()
    await sleep(280)
  }
}

async function navigateToPredix(router) {
  if (!router || isOnPredixRoute(router)) return
  try {
    await router.push(PREDIX_TOUR_ROUTE)
  } catch (e) {
    /* ignore */
  }
}

function attachDestroyHandler(username, markComplete) {
  return () => {
    if (markComplete) markUserTourCompleted(username)
    if (activeDriver) {
      activeDriver.destroy()
      activeDriver = null
    }
  }
}

async function startResultsTour(options = {}) {
  const { username = '', markComplete = true } = options
  await showPredictionsTabForTour()
  await waitForSelector('[data-tour="predix-alloy-predictions"]')
  destroyActiveTour()

  activeDriver = driver({
    ...driverDefaults(),
    steps: [],
    onDestroyStarted: attachDestroyHandler(username, markComplete),
  })

  const steps = buildResultSteps(activeDriver, { username, markComplete })
  if (!steps.length) {
    if (markComplete) markUserTourCompleted(username)
    destroyActiveTour()
    return null
  }
  activeDriver.setSteps(steps)
  activeDriver.drive()
  return activeDriver
}

async function startPredixPageTour(options = {}) {
  const { username = '', markComplete = true } = options
  await waitForSelector('[data-tour="predix-intro"]')
  await sleep(350)
  await expandPredixCompositionPanel()
  await showMainDbForTour()
  // Wait for Main DB inputs (not Benchmark) so step 3 is not skipped by materializeSteps
  await waitForSelector('[data-tour="predix-main-inputs"]', { timeout: 20000 })
  await waitForSelector('[data-tour="predix-start-prediction"]', { timeout: 5000 })
  destroyActiveTour()

  activeDriver = driver({
    ...driverDefaults(),
    steps: [],
    onDestroyStarted: attachDestroyHandler(username, markComplete),
  })

  const steps = buildSetupSteps(activeDriver, { username, markComplete })
  if (!steps.length) {
    destroyActiveTour()
    return null
  }
  activeDriver.setSteps(steps)
  activeDriver.drive()
  return activeDriver
}

async function startMenuThenPredixTour(options = {}) {
  const { username = '', markComplete = true, router = null, prepareSidebar: prepareSidebarCb } = options
  await prepareSidebar(prepareSidebarCb)
  await waitForSelector('[data-tour="predix-menu"]')
  destroyActiveTour()

  activeDriver = driver({
    ...driverDefaults(),
    showProgress: false,
    doneBtnText: 'Open PrediX',
    steps: [
      {
        element: '[data-tour="predix-menu"]',
        popover: {
          title: 'PrediX™',
          description: 'Open PrediX™ from the sidebar to predict alloy properties from composition.',
          side: 'right',
          align: 'center',
          onNextClick: async (_el, _step, { driver: d }) => {
            d.destroy()
            activeDriver = null
            await navigateToPredix(router)
            await startPredixPageTour({ username, markComplete, router })
          },
          onCloseClick: (_el, _step, { driver: d }) => {
            if (markComplete) markUserTourCompleted(username)
            d.destroy()
            activeDriver = null
          },
        },
      },
    ],
  })
  activeDriver.drive()
  return activeDriver
}

/**
 * - Not on Predix → PrediX in menu first, then page tour
 * - On Predix → start from Welcome to PrediX™
 */
export async function startUserTour(options = {}) {
  const { router = null } = options
  destroyActiveTour()
  if (isOnPredixRoute(router)) return startPredixPageTour(options)
  return startMenuThenPredixTour(options)
}
